import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { BiVizDashboardDecoActivitySqlMap } from '../sql-maps/biviz-dashboard-deco-activity-datasource-sql-map';

export interface DecoActivityByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** Per-calendar-date loader for decoration inflow / new placed decos (dashboard_deco_activity). */
export class DecoActivityByDateDataLoaderFactory extends Factory<
    DataLoader,
    DecoActivityByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DecoActivityByDateDataLoaderFactory';

    getInstance(options: DecoActivityByDateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const decoSqlMap = BiVizDashboardDecoActivitySqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    decoSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const decoSQL =
                    BiVizDashboardDecoActivitySqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;

                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );

                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                    );

                // metricName -> source delta column on dashboard_deco_activity
                const metrics = [
                    { name: 'decoration_inflow', delta: 'inflow_delta' },
                    { name: 'new_decos_placed', delta: 'new_placed_delta' },
                ];

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH dev AS
(
  SELECT *
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      decoSqlMap.dataset,
      decoSqlMap.tableId
  )}\` ds
)

, decoAggBase AS
(
  SELECT
    ds.*,
    ${decoSQL.splitSelect}
    ${decoSQL.datasourceSpecificSplitSelect}
    ${decoSQL.cohortSplitSelect}
  FROM dev ds
  ${decoSQL.join}
  ${decoSQL.where}
)

, decoAgg AS
(
  SELECT ${decoSQL.splitGroupBy}${decoSQL.dateTrunc()} AS date
    ${metrics
        .map((m) => `, SUM(${m.delta}) AS ${m.name}`)
        .join('\n    ')}

    ${metrics
        .map(
            (m) =>
                `, APPROX_QUANTILES(${m.delta}, 100)[OFFSET(50)] AS median_${m.name}_per_active_user`
        )
        .join('\n    ')}

  FROM decoAggBase
  GROUP BY ALL
)

, active_users AS
(
  SELECT
    ${decoSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    date
    , sum_daily_active_users
  FROM (
    SELECT
      ${decoSQL.cohortSplitGroupBy}${decoSQL.dateTrunc()} AS date
      , SUM(daily_active_users) AS sum_daily_active_users
    FROM
    (
      SELECT
        ${
            usersdashActivitySQL.splitGroupBy != ''
                ? `${usersdashActivitySQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )}`
                : ''
        }date
        , COUNT(DISTINCT player_id) AS daily_active_users
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          usersdashActivity.sqlMap.dataset,
          usersdashActivity.sqlMap.tableId
      )}\` ds
          ${usersdashActivitySQL.join}
          ${usersdashActivitySQL.where}
      GROUP BY ${decoSQL.cohortSplitGroupBy}date
    )
    GROUP BY ${decoSQL.cohortSplitGroupBy}date
  )
  ${
      decoSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${decoSQL.datasourceSpecificSplitGroupAlias} FROM decoAggBase)`
          : ''
  }
)

SELECT
${
    decoSQL.splitGroupBy !== ''
        ? `decoAgg.split_group AS split_group, `
        : ''
}decoAgg.date AS date
  , active_users.sum_daily_active_users
  ${metrics
      .map((m) => `, decoAgg.${m.name}`)
      .join('\n  ')}

  ${metrics
      .map((m) => `, decoAgg.median_${m.name}_per_active_user`)
      .join('\n  ')}

  ${metrics
      .map(
          (m) =>
              `, ROUND(SAFE_DIVIDE(decoAgg.${m.name}, sum_daily_active_users), 2) AS avg_${m.name}_per_active_user`
      )
      .join('\n  ')}

FROM decoAgg
LEFT JOIN active_users
ON decoAgg.date = active_users.date
${
    decoSQL.splitGroupBy !== ''
        ? `AND decoAgg.split_group = active_users.split_group`
        : ''
}
ORDER BY ${
                    decoSQL.splitGroupBy !== ''
                        ? `decoAgg.split_group, `
                        : ''
                }decoAgg.date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
